import { queryClient } from '..';
import { useMutation } from '@tanstack/react-query';
import { api } from 'api';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import { SessionData } from 'types/session';

import { useAppDispatch } from 'constants/redux';
import { useErrorHandledQuery } from 'hooks/useErrorHandledQuery';
import { useToken } from 'lib/common';
import { throwError } from 'redux/actions/messages';

export const useLoginInitMutation = () => {
    const dispatch = useAppDispatch();

    return useMutation({
        mutationFn: async (params: { phoneNumber: string }) => {
            await api.post(`/sessions/otp/init`, params);
            return params.phoneNumber;
        },
        onError: (error) => {
            const customError = error as AxiosError;

            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useLoginVerifyMutation = () => {
    const dispatch = useAppDispatch();

    return useMutation({
        mutationFn: async (params: { phoneNumber: string; code: string }) => {
            const { data } = await api.post(`/sessions/otp/verify`, params);

            return data.response;
        },
        onError: (error) => {
            const customError = error as AxiosError;

            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useLoginUsernameMutation = ({ onSuccess, onError }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    type Params = {
        username: string;
        password: string;
        phoneNumber: string;
    };

    return useMutation({
        mutationFn: async (params: Params) => {
            const { data } = await api.post(`/sessions`, params);

            return data.response;
        },
        onSuccess: () => {
            if (onSuccess) {
                onSuccess();
            }
        },
        onError: (error) => {
            if (onError) {
                onError();
            }
            const customError = error as AxiosError;

            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useCommunityCodeVerifyMutation = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const token = useToken();

    return useMutation({
        mutationFn: async (params: { setupCode: string; latitude: number; longitude: number }) => {
            await api.post(`/users/join-branch`, params, {
                headers: { authorization: token },
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['session'],
            });
        },
        onError: (error) => {
            const customError = error as AxiosError;

            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useSessionData = () => {
    const token = useToken();
    return useErrorHandledQuery<SessionData>(
        ['session'],
        async (): Promise<SessionData> => {
            const { data } = await api.get('/sessions', {
                headers: { authorization: token },
            });
            return data.response;
        },
        {
            staleTime: 300000,
        }
    );
};
